import React, { FC, useCallback, useRef, useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Episode } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { useWidget } from '@/shared/hooks/useWidget';
import { playerSeriesStructureWidget } from '@/scenarios/movie';
import { Popup } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Popup';
import { usePlayerInstance } from '@/scenarios/legacy-player/lib/contexts/instance';
import { useStartPlayingContent } from '@/scenarios/legacy-player/components/player-manager/hooks/useStartPlayingContent';

import { BaseControlProps } from '../../../types/controls';
import { usePlayerPopupButton } from '../../../hooks/skin/use-player-popup-button';

import styles from './styles.module.css';

interface Props extends BaseControlProps {
    filmId: string;
    seasonId: string;
    onSelectEpisode?: () => void;
    className?: string;
}

export const EpisodeNavigatorPopupButton: FC<Props> = ({
    contentId,
    filmId,
    seasonId,
    className,
    onClick,
    onSelectEpisode,
}) => {
    const PlayerSeriesStructure = useWidget(playerSeriesStructureWidget);
    const instance = usePlayerInstance();
    const controlRef = useRef<HTMLDivElement>(null);
    const [isControlHovered, setIsControlHovered] = useState(false);
    const [currentEpisodeTiming, setCurrentEpisodeTiming] = useState(instance?.currentTime || 0);
    const [isOpen, setIsOpen] = useState(false);
    const { handleClick } = usePlayerPopupButton({
        isOpen,
        setIsOpen,
        onClick,
    });

    const startContent = useStartPlayingContent();

    const onSelectEpisodeCallback = useCallback(
        (episode: Episode) => {
            startContent({
                contentId: episode.contentId ?? '',
                tvSeries: {
                    seriesNumber: episode.number,
                    seasonNumber: episode.season?.number,
                },
            });
            setIsOpen(false);
            onSelectEpisode?.();
        },
        [onSelectEpisode, startContent]
    );

    return (
        <div
            onMouseEnter={() => {
                setCurrentEpisodeTiming(instance?.currentTime || 0);
                setIsControlHovered(true);
            }}
            onMouseLeave={() => setIsControlHovered(false)}
            ref={controlRef}
            className={classnames(styles.root, className)}
        >
            <button
                onClick={handleClick}
                className={styles.episodeNavigatorButton}
                aria-expanded={isOpen}
            >
                <FormattedMessage
                    id="features.player.9d25fa6be48dcf0506d75825b3152c4e"
                    defaultMessage="Seasons and episodes"
                    description="Кнопка сезоны и серии в попапе навигации плеера для сериалов"
                />
            </button>
            <Popup
                targetHovered={isControlHovered}
                lockTargetHover={setIsOpen}
                visible={isOpen}
                targetRef={controlRef}
                placement="top"
                enableSeamlessResize
            >
                <PlayerSeriesStructure
                    filmId={filmId}
                    seasonId={seasonId}
                    currentEpisodeTiming={currentEpisodeTiming}
                    playingContentId={contentId}
                    onEpisodeNavigate={onSelectEpisodeCallback}
                />
            </Popup>
        </div>
    );
};
